import { useCallback } from "react";
import { addOrRemove } from "@theorgchartdev/storybook";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { GetWSMgr } from "@/Utils/StateLib";
import {
  AssignmentStatus,
  enumObjClassNames,
  enumObjectTypes,
  kEmployeeAssignmentStatusID
} from "@coreapp/constants";
import { FindObject } from "@/appComponents/Blocks/Modals/RecordEditorModal/PositionEditorUtils";

type EmployeeAssignmentHookOptions = {
  assignedEmployees: QObjDBObject[];
  setAssignedEmployees: React.Dispatch<React.SetStateAction<QObjDBObject[]>>;
  selectedEmployees?: string[];
  setSelectedEmployees?: React.Dispatch<React.SetStateAction<string[]>>;
  currentPosition?: QObjDBObject;
  overrides?: QObjDBObject[];
  GetHolder?: (employeeData: any) => QObjDBObject;
  onConflict?: (conflicts: QObjDBObject[]) => void;
  immediateConflictDetection?: boolean; // If true, show conflicts immediately on assignment
};

export const useEmployeeAssignment = ({
  assignedEmployees,
  setAssignedEmployees,
  selectedEmployees,
  setSelectedEmployees,
  currentPosition,
  overrides = [],
  GetHolder,
  onConflict,
  immediateConflictDetection = true
}: EmployeeAssignmentHookOptions) => {
  const wsMgr = GetWSMgr();
  const planManager = wsMgr?.GetPlanMgr();

  /**
   * Loads detailed employee data if not already a QObjDBObject
   */
  const loadEmployeeDetails = useCallback(
    async (employeeId: string, holderObj: QObjDBObject): Promise<QObjDBObject> => {
      return new Promise(resolve => {
        if (!planManager) {
          resolve(holderObj);
          return;
        }

        (planManager as any).LoadRecords({ recordIDs: [employeeId] }, (response: any) => {
          if (response?.data?.[0] && GetHolder) {
            const fullHolderObj = GetHolder(response.data[0]);
            resolve(fullHolderObj);
          } else {
            resolve(holderObj);
          }
        });
      });
    },
    [planManager, GetHolder]
  );

  /**
   * Checks if an employee has assignment conflicts
   */
  const checkEmployeeConflicts = useCallback(
    (employeeData: QObjDBObject): boolean => {
      const status = employeeData.SmartGetFieldValue(kEmployeeAssignmentStatusID);
      return status !== AssignmentStatus.Unassigned;
    },
    [overrides]
  );

  /**
   * Gets current position details for an employee
   */
  const getCurrentPositionDetails = useCallback(
    (holderObj: QObjDBObject) => {
      const currentPositionId = holderObj.SmartGetFieldValue("Employee PositionID") as string;
      const currentPositionTitle = holderObj.SmartGetFieldValue("Position Title");

      if (
        !currentPositionId ||
        currentPositionId === currentPosition?.SmartGetFieldValue("Position ID")
      ) {
        return null;
      }

      let foundPosition = FindObject(currentPositionId, enumObjClassNames.position);
      if (!foundPosition && planManager) {
        foundPosition = planManager.GetCacheBaseRecord(
          enumObjectTypes.position,
          String(currentPositionId)
        );
      }

      return {
        id: currentPositionId,
        title:
          foundPosition?.SmartGetFieldValue("Position Title") ||
          currentPositionTitle ||
          "Unknown Position",
        position: foundPosition
      };
    },
    [currentPosition, planManager]
  );

  /**
   * Handles adding an employee with conflict detection
   */
  const handleAddEmployee = useCallback(
    async (employeeData: Record<string, unknown> | QObjDBObject) => {
      if (!GetHolder) {
        console.error("GetHolder function is required for employee assignment");
        return;
      }

      let holderObj: QObjDBObject;

      // Convert to QObjDBObject if needed
      if (employeeData instanceof QObjDBObject) {
        holderObj = employeeData;
      } else {
        holderObj = GetHolder(employeeData);

        const override = planManager?.fOverrideClass?.GetExistingBoxOverride(holderObj);
        if (override) {
          override.ApplyChangesReadOnly(holderObj);
        }
      }

      // Extract ID from the holder object
      const employeeId = String(
        holderObj.SmartGetFieldValue("EmployeeID") ||
          holderObj.SmartGetFieldValue("RecordID") ||
          holderObj.SmartGetFieldValue("ID")
      );

      if (!employeeId || employeeId === "undefined") {
        console.error("Could not extract employee ID from holder object");
        return;
      }

      // Check if already assigned to prevent duplicates
      const isAlreadyAssigned = assignedEmployees.some(emp => {
        const empId = String(
          emp.SmartGetFieldValue("EmployeeID") ||
            emp.SmartGetFieldValue("RecordID") ||
            emp.SmartGetFieldValue("ID")
        );
        return empId === employeeId;
      });

      if (isAlreadyAssigned) {
        return;
      }

      // Check for conflicts only if immediate detection is enabled
      if (immediateConflictDetection) {
        const hasConflict = checkEmployeeConflicts(holderObj);

        if (hasConflict && onConflict) {
          // Handle conflict with the new modal
          onConflict([holderObj]);
          return;
        }
      }

      // No conflicts (or conflict detection disabled), proceed with assignment
      setAssignedEmployees(prev => [...prev, holderObj]);

      // Load detailed data if needed (for search results)
      if (!(employeeData instanceof QObjDBObject)) {
        const detailedHolderObj = await loadEmployeeDetails(employeeId, holderObj);

        // Apply overrides to the detailed object too
        const detailedOverride =
          planManager?.fOverrideClass?.GetExistingBoxOverride(detailedHolderObj);
        if (detailedOverride) {
          detailedOverride.ApplyChangesReadOnly(detailedHolderObj);
        }

        setAssignedEmployees(prev =>
          prev.map(emp => {
            const empId = String(
              emp.SmartGetFieldValue("EmployeeID") ||
                emp.SmartGetFieldValue("RecordID") ||
                emp.SmartGetFieldValue("ID")
            );
            return empId === employeeId ? detailedHolderObj : emp;
          })
        );
      }
    },
    [
      assignedEmployees,
      setAssignedEmployees,
      GetHolder,
      checkEmployeeConflicts,
      onConflict,
      loadEmployeeDetails,
      immediateConflictDetection
    ]
  );

  /**
   * Handles removing an employee
   */
  const handleRemoveEmployee = useCallback(
    (employeeData: string | Record<string, unknown> | QObjDBObject) => {
      let employeeId: string;

      if (typeof employeeData === "string") {
        employeeId = employeeData;
      } else {
        // Extract ID from employee object
        if (employeeData instanceof QObjDBObject) {
          employeeId = String(
            employeeData.SmartGetFieldValue("EmployeeID") ||
              employeeData.SmartGetFieldValue("RecordID") ||
              employeeData.SmartGetFieldValue("ID")
          );
        } else if (GetHolder) {
          // Use GetHolder to convert and then extract ID
          const holderObj = GetHolder(employeeData);
          employeeId = String(
            holderObj.SmartGetFieldValue("EmployeeID") ||
              holderObj.SmartGetFieldValue("RecordID") ||
              holderObj.SmartGetFieldValue("ID")
          );
        } else {
          // Fallback to direct property access
          employeeId = String(
            (employeeData as any).EmployeeID ||
              (employeeData as any).RecordID ||
              (employeeData as any).ID
          );
        }
      }

      if (!employeeId || employeeId === "undefined") {
        console.error("Could not extract employee ID for removal");
        return;
      }

      setAssignedEmployees(prev =>
        prev.filter(emp => {
          const empId = String(
            emp.SmartGetFieldValue("EmployeeID") ||
              emp.SmartGetFieldValue("RecordID") ||
              emp.SmartGetFieldValue("ID")
          );
          return empId !== employeeId;
        })
      );
    },
    [setAssignedEmployees, GetHolder]
  );

  /**
   * Handles toggling employee selection (for EmployeeList usage)
   */
  const handleEmployeeToggle = useCallback(
    async (employeeId: string, employeeData: Record<string, unknown> | QObjDBObject) => {
      if (!setSelectedEmployees) {
        console.error("setSelectedEmployees is required for employee toggle");
        return;
      }

      setSelectedEmployees(prevSelected => {
        const newSelected = addOrRemove(prevSelected, `${employeeId}`);

        if (newSelected.includes(employeeId) && !prevSelected.includes(employeeId)) {
          // Adding employee - pass the full employee data
          handleAddEmployee(employeeData);
        } else if (!newSelected.includes(employeeId) && prevSelected.includes(employeeId)) {
          // Removing employee - can pass just the ID
          handleRemoveEmployee(employeeId);
        }

        return newSelected;
      });
    },
    [setSelectedEmployees, handleAddEmployee, handleRemoveEmployee]
  );

  /**
   * Force assigns employees despite conflicts (for conflict modal confirmation)
   */
  const forceAssignEmployees = useCallback(
    (employees: QObjDBObject[]) => {
      setAssignedEmployees(prev => [
        ...prev,
        ...employees.filter(emp => {
          const empId = emp.SmartGetFieldValue("EmployeeID") || emp.SmartGetFieldValue("RecordID");
          return !prev.some(existing => {
            const existingId =
              existing.SmartGetFieldValue("EmployeeID") || existing.SmartGetFieldValue("RecordID");
            return String(existingId) === String(empId);
          });
        })
      ]);
    },
    [setAssignedEmployees]
  );

  return {
    handleAddEmployee,
    handleRemoveEmployee,
    handleEmployeeToggle,
    forceAssignEmployees,
    checkEmployeeConflicts,
    getCurrentPositionDetails,
    loadEmployeeDetails
  };
};
